import React from 'react'
import { useNavigate } from 'react-router-dom'
import { PartnerRecruitment } from '../../clients/kyousou_api_client'
import BaseCard from './base_card'
import LoadingSpinner from '../loading/loading_spinner'
import {
  RecruitmentStatus,
  RecruitmentStatusLabel,
} from '../../enums/RecruitmentStatus'
import { SDGsCategoryLabel } from '../../enums/SDGsCategory'
import { formatDate } from '../../utils/datetime_util'

interface PartnerRecruitmentManageCardProps {
  recruitment: PartnerRecruitment
  initialized: boolean
}

/**
 * 投稿済みカードコンポーネント
 * 編集ボタン付きのカードを表示
 */
const PartnerRecruitmentManageCard: React.FC<
  PartnerRecruitmentManageCardProps
> = ({ recruitment, initialized }) => {
  const navigate = useNavigate()

  // カードクリック時の処理（編集ページへ遷移）
  const handleCardClick = (e: React.MouseEvent) => {
    // 編集ボタンのクリックではない場合のみ編集ページへ遷移
    if (!(e.target as HTMLElement).closest('.edit-button')) {
      navigate(`/recruitment/${recruitment.id}/edit`)
    }
  }

  // 編集ボタンクリック時の処理
  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    navigate(`/recruitment/${recruitment.id}/edit`)
  }

  // ステータスに応じたラベルを取得
  const getStatusLabel = () => {
    return recruitment.status
      ? RecruitmentStatusLabel[recruitment.status]
      : '公開中'
  }

  // ステータスに応じたバッジの色を取得
  const getStatusColor = () => {
    if (recruitment.status === RecruitmentStatus.DRAFT)
      return 'bg-main-darkgray'
    if (recruitment.status === RecruitmentStatus.SCHEDULED)
      return 'bg-main-orange'
    return 'bg-main-green'
  }

  // HTMLタグを除去してプレーンテキストに変換し、3行に制限
  const getDescriptionPreview = (htmlContent: string) => {
    // HTMLタグを除去
    const textContent = htmlContent.replace(/<[^>]*>/g, '')
    // 改行で分割して最初の3行を取得
    const lines = textContent
      .split('\n')
      .filter((line) => line.trim().length > 0)
    const previewLines = lines.slice(0, 3)

    // 3行より多い場合は...を追加
    if (lines.length > 3) {
      return previewLines.join('\n') + '...'
    }
    return previewLines.join('\n')
  }

  return (
    <div className="relative">
      <BaseCard onClick={handleCardClick} className="h-full">
        <>
          {initialized ? (
            <>
              <div className="flex flex-col h-full">
                <div className="relative">
                  {recruitment.thumbnail_url ? (
                    <img
                      src={recruitment.thumbnail_url}
                      alt="募集サムネイル"
                      className="w-full h-48 object-cover rounded mb-3 bg-main-gray"
                    />
                  ) : (
                    <div className="w-full h-48 bg-main-gray rounded mb-3 flex items-center justify-center">
                      <span className="material-icons !text-6xl text-main-darkgray">
                        image
                      </span>
                    </div>
                  )}
                  {/* ステータスバッジ */}
                  <div
                    className={`absolute top-2 right-2 px-2 py-1 leading-6 text-white rounded ${getStatusColor()}`}
                  >
                    {getStatusLabel()}
                  </div>
                </div>

                {/* タイトルセクション - 2行分の高さを確保 */}
                <h3 className="font-bold text-lg mb-1 line-clamp-2">
                  {recruitment.title}
                </h3>

                {/* 日付セクション - 常に2行分の高さを確保 */}
                <div className="mb-1">
                  {/* 予約投稿日の表示 */}
                  {recruitment.status === 'scheduled' &&
                    recruitment.scheduled_date && (
                      <div className="text-xs text-main-green mb-1 flex items-center gap-1">
                        <span className="material-icons !text-base align-middle">
                          schedule
                        </span>
                        予約投稿日：
                        {formatDate(recruitment.scheduled_date)}
                      </div>
                    )}
                  <div className="text-xs text-main-darkgray flex items-center gap-1">
                    <span className="material-icons !text-base align-middle">
                      calendar_today
                    </span>
                    公開日：
                    {(() => {
                      // 予約投稿の場合は予約日を表示
                      if (
                        recruitment.status === 'scheduled' &&
                        recruitment.scheduled_date
                      ) {
                        return formatDate(recruitment.scheduled_date)
                      }
                      // 公開中の場合は作成日を表示
                      if (
                        recruitment.status === 'published' &&
                        recruitment.created_at
                      ) {
                        return formatDate(recruitment.created_at)
                      }
                      // 下書きの場合は「-」を表示
                      return '-'
                    })()}
                  </div>
                </div>

                {/* 説明文セクション - 3行分の高さを確保 */}
                <p className="text-sm mb-3 whitespace-pre-line line-clamp-3">
                  {getDescriptionPreview(recruitment.description || '')}
                </p>

                {/* SDGsタグセクション - 最小高さを設定 */}
                <div className="flex flex-wrap gap-2 mb-3 min-h-[2rem]">
                  {recruitment.sdgs_categories?.map((cat, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 rounded-full border border-main-green text-main-green text-xs font-bold bg-main-white h-fit"
                    >
                      目標 {cat}: {SDGsCategoryLabel[cat] || ''}
                    </span>
                  ))}
                </div>

                {/* 企業情報 - mt-autoで下部に固定 */}
                <div className="mt-auto">
                  {/* 黒バー */}
                  <div className="border-b border-main-black my-4"></div>

                  <div className="flex items-center gap-2 mb-3">
                    <img
                      src={recruitment.enterprise.logoUrl}
                      alt="企業ロゴ"
                      className="w-12 h-12 rounded object-cover"
                    />
                    <div>
                      <p className="text-base font-bold line-clamp-1">
                        {recruitment.enterprise?.name}
                      </p>
                    </div>
                  </div>
                </div>

                {/* 編集ボタン */}
                <button
                  className="edit-button w-full bg-main-green hover:bg-main-lightgreen text-white font-bold py-3 px-4 rounded transition-colors"
                  onClick={handleEditClick}
                >
                  編集
                </button>
              </div>
            </>
          ) : (
            <LoadingSpinner className="my-20" />
          )}
        </>
      </BaseCard>
    </div>
  )
}

export default PartnerRecruitmentManageCard
